/**
 * The replay camera — a cut sequence, not a chase cam.
 *
 * `GameCamera` is deliberately not reused. It exists to keep the car you are
 * driving readable, which is the opposite of what a replay wants: the ball has
 * already gone in, nobody is driving, and the interesting information is the
 * SHAPE of the play. So this is a director rather than a boom — three fixed
 * ideas about where to stand, cut between on a schedule, each aiming at the
 * ball.
 *
 * All geometry is in the PHYSICS frame (right-handed, +Z up), exactly like
 * ./camera, and converts to Three exactly once at the bottom of `writeCamera`.
 *
 * THE SCHEDULE IS TOTAL. `shotAt` answers for every finite time, including
 * negatives and anything past the last cut, because a director with a gap in
 * its schedule shows a black frame and the bug reproduces once every few goals.
 *
 * SMOOTHING is the same exponential law as everywhere else — `1 - exp(-k dt)`,
 * frame-rate independent — EXCEPT across a cut, where the camera snaps. A cut
 * that eases is not a cut; it is a very fast pan, and it reads as a mistake.
 */

import { PerspectiveCamera, Vector3 } from 'three';
import { ARENA } from '../physics/constants';
import { GOAL } from '../physics/arena-data';
import { clamp, set, vec3, type Vec3 } from '../physics/math';
import { horizontalToVerticalFov, smoothFactor } from './camera';
import { toThree } from './convert';
import { REPLAY_OUTPUT_SECONDS, type ReplayCarPose, type ReplayPose } from './replay';

// ---------------------------------------------------------------------------
// The shot list
// ---------------------------------------------------------------------------

export type ReplayShotKind =
  /** High wide arc swinging around the ball's path. Establishes the play. */
  | 'orbit'
  /** Low and behind the scorer, looking down their shot. */
  | 'chase'
  /** Inside the net, looking back out at the ball arriving. */
  | 'goalmouth';

export interface ReplayShot {
  kind: ReplayShotKind;
  /** Playback time this shot ENDS at, in seconds. Strictly increasing. */
  until: number;
}

/**
 * Cut times are chosen against ./replay's schedule, not independently: the cut
 * to `chase` lands where the playback rate drops to 0.55, and the cut to
 * `goalmouth` lands where it drops to 0.25. Every cut therefore coincides with
 * a change of pace, which is what stops the sequence feeling arbitrary.
 */
export const REPLAY_SHOTS: readonly ReplayShot[] = [
  { kind: 'orbit', until: 2.4 },
  { kind: 'chase', until: 4.0 },
  { kind: 'goalmouth', until: REPLAY_OUTPUT_SECONDS },
];

/**
 * Index of the shot on screen at playback time `t`.
 *
 * Total: `t <= 0` and non-finite `t` take the first shot, anything at or past
 * the final cut takes the last. Deterministic: no state, no clock, no random.
 */
export const shotAt = (t: number): number => {
  // NaN compares false against everything, so it is the one value the loop
  // below cannot decide; it takes the first shot. +Infinity falls through to
  // the last, which is exactly right.
  if (Number.isNaN(t) || t <= 0) return 0;
  for (let i = 0; i < REPLAY_SHOTS.length; i++) {
    if (t < REPLAY_SHOTS[i].until) return i;
  }
  return REPLAY_SHOTS.length - 1;
};

/** Playback time the shot at `index` begins. */
export const shotStart = (index: number): number =>
  index <= 0 ? 0 : REPLAY_SHOTS[Math.min(index, REPLAY_SHOTS.length - 1) - 1].until;

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

const TUNING = {
  /** Orbit radius and height above the ball, uu. */
  orbitRadius: 2400,
  orbitHeight: 950,
  /** How much of the ball's own offset the orbit pivot keeps. */
  orbitPivotPull: 0.55,
  /** Radians per second the orbit sweeps. Slow — this is a drift, not a spin. */
  orbitRate: 0.42,
  /** Chase shot: setback along the car's own forward axis, and lift. */
  chaseBack: 620,
  chaseLift: 240,
  chaseSide: 190,
  /**
   * Goal-mouth shot: how far past the goal line, how high, and how far off the
   * centre line.
   *
   * The side offset is the whole shot. Measured in the browser: parked on the
   * centre line, the camera finishes 330 uu from a ball whose diameter is 182,
   * lit from the far side — the ball fills 60% of the frame with its own
   * shadowed back and the replay ends on what looks like a rendering failure.
   * Standing in the corner of the net puts the ball ~900 uu away and moving
   * ACROSS the frame, which is the shot this was always supposed to be.
   *
   * The depth keeps the 1792 uu mouth wider than the ~1350 uu the 88-degree
   * view covers at that range, so the opening still frames the whole picture
   * and none of the unlit interior creeps into the corners.
   */
  mouthDepth: 700,
  mouthHeight: 470,
  mouthSide: 560,
  /** Position and aim smoothing rates, 1/s. Aim tracks faster than the body. */
  positionRate: 5.5,
  aimRate: 9,
  /** Horizontal FOV, RL slider space. Tighter than gameplay: this is cinema. */
  fov: 88,
  /** Keep the camera off the floor, out of the ceiling and off the walls. */
  floorMargin: 60,
  ceilingMargin: 60,
  wallMargin: 240,
} as const;

const _v = new Vector3();

/**
 * Pull a camera position back inside the playable volume.
 *
 * Necessary, not defensive. An orbit of radius 2400 around a ball that is
 * sitting in the goal mouth puts the camera 2000 uu behind the back wall for a
 * third of its sweep — and a camera outside the shell sees the arena's back
 * faces, which are culled, so the shot is not "outside looking in", it is a
 * black frame. The clamp slides the camera along the wall instead; it keeps
 * looking at the same thing, from as close to the angle it wanted as the
 * building allows.
 *
 * The corner cut is the same 45-degree constraint the arena uses (|x| + |y| <=
 * cornerPlaneIntercept), resolved along its own normal.
 */
export const containCamera = (p: Vec3): Vec3 => {
  const m = TUNING.wallMargin;
  p.x = clamp(p.x, -(ARENA.halfWidth - m), ARENA.halfWidth - m);
  p.y = clamp(p.y, -(ARENA.halfLength - m), ARENA.halfLength - m);
  p.z = clamp(p.z, ARENA.floor + TUNING.floorMargin, ARENA.ceiling - TUNING.ceilingMargin);

  const corner = ARENA.cornerPlaneIntercept - m * Math.SQRT2;
  const over = Math.abs(p.x) + Math.abs(p.y) - corner;
  if (over > 0) {
    p.x -= Math.sign(p.x) * over * 0.5;
    p.y -= Math.sign(p.y) * over * 0.5;
  }
  return p;
};

// ---------------------------------------------------------------------------
// ReplayCamera
// ---------------------------------------------------------------------------

export interface ReplayCameraOptions {
  camera?: PerspectiveCamera;
  /** Horizontal FOV in RL slider space. */
  fov?: number;
}

export class ReplayCamera {
  readonly camera: PerspectiveCamera;

  /** Smoothed camera position, PHYSICS frame. Treat as read-only. */
  readonly position: Vec3 = vec3();
  /** Smoothed look target, PHYSICS frame. Treat as read-only. */
  readonly target: Vec3 = vec3();

  private readonly fov: number;
  private readonly desired: Vec3 = vec3();
  private readonly aim: Vec3 = vec3();
  private readonly scratchForward: Vec3 = vec3();

  /** +1 when the ball went in at +Y, -1 at -Y. Set by `begin`. */
  private goalSign = 1;
  /** Index into `world.cars` of the car to chase, or null for an own goal. */
  private scorer: number | null = null;
  /** Where the orbit starts, so a clip is reproducible from its own data. */
  private orbitPhase = 0;
  /** Which post the goal-mouth camera stands behind: away from the ball. */
  private mouthSide = 1;
  private shot = -1;
  private appliedVerticalFov = -1;

  constructor(options: ReplayCameraOptions = {}) {
    this.fov = options.fov ?? TUNING.fov;
    this.camera =
      options.camera ?? new PerspectiveCamera(horizontalToVerticalFov(this.fov, 16 / 9), 16 / 9, 10, 30000);
  }

  /** Index of the shot currently on screen, or -1 before the first `update`. */
  get currentShot(): number {
    return this.shot;
  }

  /**
   * Aim at a fresh clip.
   *
   * `orbitPhase` is derived from the goal, not drawn from a generator: the same
   * goal always opens from the same angle, which is what makes a replay
   * reproducible without the camera needing a seed of its own.
   */
  begin(team: 0 | 1, scorer: number | null, ballX: number): void {
    // Blue (team 0) scores in the goal at +Y.
    this.goalSign = team === 0 ? 1 : -1;
    this.scorer = scorer;
    // Open from whichever side of the pitch the ball finished on, so the first
    // frame contains the play rather than the empty half behind it.
    this.orbitPhase = Math.atan2(this.goalSign, ballX >= 0 ? 1 : -1);
    // Stand behind the FAR post, so the ball crosses the frame instead of
    // arriving down the lens. Decided once per clip rather than per frame: a
    // side that flips as the ball drifts across the centre line would swing the
    // whole shot mid-take.
    this.mouthSide = ballX >= 0 ? -1 : 1;
    this.shot = -1;
  }

  /**
   * Advance and write into the Three.js camera.
   *
   * `t` is PLAYBACK time in seconds (the replay clock, which is what the cuts
   * are scheduled against), and `dt` is the real frame delta.
   */
  update(t: number, pose: ReplayPose, dt: number): void {
    const shot = shotAt(t);
    const cut = shot !== this.shot;
    this.shot = shot;

    this.frameShot(REPLAY_SHOTS[shot].kind, t, pose);

    const step = Number.isFinite(dt) ? clamp(dt, 0, 0.25) : 0;
    // A cut snaps. Everything else eases.
    const kPos = cut ? 1 : smoothFactor(TUNING.positionRate, step);
    const kAim = cut ? 1 : smoothFactor(TUNING.aimRate, step);

    this.position.x += (this.desired.x - this.position.x) * kPos;
    this.position.y += (this.desired.y - this.position.y) * kPos;
    this.position.z += (this.desired.z - this.position.z) * kPos;
    this.target.x += (this.aim.x - this.target.x) * kAim;
    this.target.y += (this.aim.y - this.target.y) * kAim;
    this.target.z += (this.aim.z - this.target.z) * kAim;

    // The smoothed position can lag outside on the frame after a cut, so the
    // floor and ceiling are re-asserted on the SMOOTHED value, not only on the
    // target. Nothing else is: the goal-mouth shot is legitimately outside.
    this.position.z = clamp(
      this.position.z,
      ARENA.floor + TUNING.floorMargin,
      ARENA.ceiling - TUNING.ceilingMargin
    );

    this.writeCamera();
  }

  // -------------------------------------------------------------------------
  // The shots
  // -------------------------------------------------------------------------

  /** Fill `desired` and `aim` for one shot. Pure in everything but the writes. */
  private frameShot(kind: ReplayShotKind, t: number, pose: ReplayPose): void {
    const ball = pose.ball;
    set(this.aim, ball.x, ball.y, ball.z);

    if (kind === 'orbit') {
      // Orbit a pivot pulled back toward the middle of the pitch rather than
      // the ball itself. A goal ends with the ball in the mouth, and a circle
      // centred there spends most of its sweep in the back wall — `containCamera`
      // would then flatten the arc into a slide along it. Off the centre, the
      // whole circle fits and the shot keeps its swing.
      const angle = this.orbitPhase + t * TUNING.orbitRate;
      set(
        this.desired,
        ball.x * TUNING.orbitPivotPull + Math.cos(angle) * TUNING.orbitRadius,
        ball.y * TUNING.orbitPivotPull + Math.sin(angle) * TUNING.orbitRadius,
        ball.z + TUNING.orbitHeight
      );
      containCamera(this.desired);
      return;
    }

    if (kind === 'chase') {
      const car = this.chaseCar(pose);
      if (car) {
        const f = poseForward(car, this.scratchForward);
        set(
          this.desired,
          car.position.x - f.x * TUNING.chaseBack - f.y * TUNING.chaseSide,
          car.position.y - f.y * TUNING.chaseBack + f.x * TUNING.chaseSide,
          car.position.z + TUNING.chaseLift
        );
      } else {
        // Own goal, or the scorer got demolished on the way: fall back to a low
        // wide shot from the attacking half, still looking at the ball.
        set(this.desired, ball.x * 0.4 + 1500, ball.y - this.goalSign * 2100, ball.z + 420);
      }
      // A car shooting from close range has its "behind" past the goal line,
      // and a chase cam that follows it into the net is a shot of the netting.
      containCamera(this.desired);
      return;
    }

    // Goal mouth: in the corner of the net, up near the bar, looking back out.
    // The offsets keep the camera inside the goal box (|x| < 896, z < 640),
    // which is the one piece of open space outside the playable volume — so
    // this shot, and only this shot, skips `containCamera`.
    set(
      this.desired,
      this.mouthSide * TUNING.mouthSide,
      this.goalSign * (ARENA.halfLength + TUNING.mouthDepth),
      clamp(TUNING.mouthHeight, 60, GOAL.heightMesh - 120)
    );
  }

  /**
   * The car the chase shot follows. Null when there is nobody to follow — an
   * own goal has no scorer, and a demolished car is a wreck, not a subject.
   */
  private chaseCar(pose: ReplayPose): ReplayCarPose | null {
    if (this.scorer === null) return null;
    const car = pose.cars[this.scorer];
    if (!car || car.demolished) return null;
    return car;
  }

  private writeCamera(): void {
    // --- the one and only frame conversion ---
    this.camera.up.set(0, 1, 0);
    this.camera.position.copy(toThree(this.position, _v));
    this.camera.lookAt(toThree(this.target, _v));

    const vFov = horizontalToVerticalFov(this.fov, this.camera.aspect);
    if (Math.abs(vFov - this.appliedVerticalFov) > 1e-6) {
      this.appliedVerticalFov = vFov;
      this.camera.fov = vFov;
      this.camera.updateProjectionMatrix();
    }
  }
}

// ---------------------------------------------------------------------------
// Recovering a physics-frame forward axis from a recorded pose
// ---------------------------------------------------------------------------

const _forward = new Vector3();

/**
 * The pose's forward axis, back in the PHYSICS frame.
 *
 * The buffer stores orientations as Three quaternions because that is the form
 * every other consumer wants (see `ReplayBuffer.record`), and the chase shot is
 * the one place that needs the axis in physics coordinates. Three's mesh-forward
 * is local -Z, and ./convert's inverse maps three (x, y, z) to physics
 * (x, -z, y); doing it here costs one rotation per frame instead of a second
 * copy on every recorded tick.
 */
export const poseForward = (pose: ReplayCarPose, out: Vec3): Vec3 => {
  _forward.set(0, 0, -1).applyQuaternion(pose.quaternion);
  return set(out, _forward.x, -_forward.z, _forward.y);
};
